import { Badge, IconButton, ListItemIcon, ListItemText, MenuItem, MenuList, Switch, Tooltip } from "@mui/material";
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import NotesIcon from '@mui/icons-material/Notes';
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { PAGE_TYPE } from "../../../types/pages.type";
import PickProjectionDaysSVG from "../../../assets/svgs/PickProjectionDaysSVG.svg";
import CompareSideIcon from "../../../assets/svgs/CompareSideIcon.svg";
import { useDispatch } from "react-redux";
import { useCallback } from "react";
import { setIsComparisonRangeSet, setRemoveCompareFilter } from "../../../store/reducers/comparisonReducers/comparison.slice";
import HtmlTooltip from "../HtmlTooltip";
import ClearIcon from "@mui/icons-material/Clear";
import { setProjectionDays } from "../../../store/reducers/common.slice";
import useTutorialTrigger from "../../../hooks/useTutorialTrigger";
import { TUTORIAL_FEATURES } from "../../../config/tutorials.config";
import { isPageConverterPage } from "../page-converter/constants";

const TooltipBadge = ({ content, onClick, children }) => (
    <Badge
        color="error"
        sx={{
            cursor: "pointer",
            "& .MuiBadge-badge": {
                minWidth: "14px",          // Smaller width for the badge
                height: "14px",            // Smaller height for the badge
                borderRadius: "50%",       // Make the badge round
                transform: "translate(50%, -50%) scale(1.2)",  // Position and scale down
                padding: 0,                // Remove padding for a snug fit
            },
        }}
        badgeContent={
            <HtmlTooltip content={content} placement="bottom-start">
                <ClearIcon onClick={onClick} sx={{ fontSize: 10 }} />
            </HtmlTooltip>
        }
    >
        {children}
    </Badge>
);

const MenuSwitchItem = ({
    icon,
    label,
    onClick,
    hasSwitch = true,
    handleClose,
    checked = false,
    handleSaveShortcut,
    name,
    isMobile = false,
    disabled = false,
}) => {
    // Function to handle the Switch toggle without triggering MenuItem's onClick
    const handleSwitchToggle = (e) => {
        e.stopPropagation(); // Prevents bubbling up to MenuItem
        handleSaveShortcut(e);
    };

    return (
        <MenuItem
            disabled={disabled}
            sx={{
                position: 'relative', // Ensures we can define hover width
                '&:hover::before': {
                    content: '""', // Required for pseudo-element
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 'calc(100% - 55px)', // Adjust the width as needed (e.g., exclude 50px on the right)
                    backgroundColor: 'rgba(0, 0, 0, 0.08)', // Hover background color
                    zIndex: 0, // Ensures it stays behind the content
                    borderTopRightRadius: '4px', // Adjust as needed for a rounded top-right border
                    borderBottomRightRadius: '4px', // Adjust as needed for a rounded bottom-right border
                    // borderTop: '1px solid rgba(0, 0, 0, 0.08)', // Border on the top
                    // borderBottom: '1px solid rgba(0, 0, 0, 0.08)', // Border on the bottom
                    // borderRight: '1px solid rgba(0, 0, 0, 0.08)', // Border on the right
                },
                '& > *': {
                    position: 'relative', // Keep content above the pseudo-element
                    zIndex: 1,
                },
                '&:hover': {
                    backgroundColor: 'transparent', // Remove background color on hover
                }
            }}
        >
            <ListItemIcon
                onClick={() => {
                    if (disabled) return;
                    onClick();
                    handleClose(); // Only close the menu when the icon is clicked
                }}
            >
                {icon}
            </ListItemIcon>
            <ListItemText
                onClick={() => {
                    if (disabled) return;
                    onClick();
                    handleClose(); // Only close the menu when the text is clicked
                }}
            >
                {label}
            </ListItemText>
            {hasSwitch && !isMobile && (
                <Tooltip title={checked ? `Remove ${label} from dashboard` : `Add ${label} to dashboard`} arrow>
                    <span>
                        <Switch
                            className="action"
                            size="small"
                            name={name}
                            checked={checked}
                            disabled={disabled}
                            onChange={handleSwitchToggle} // Separate handler for the Switch
                        />
                    </span>
                </Tooltip>
            )}
        </MenuItem>
    );
};

export default function HeaderMenuItem({
    handleClose,
    page,
    handleOnclickTimeFrameConvert,
    handleOnclickCompare,
    handleOnclickOpenAutoReport,
    handleOnclickOpenAlertReport,
    rangesSet,
    projectionDays,
    handleSaveShortcut,
    selectedSortCut,
    handleOnclickOpenNotes,
    isMobile = false,
    onPageConverter,
    pageConverterDisabled = false,
    pageConverterTransferType,
}) {

    const dispatch = useDispatch();
    const { triggerTutorial, trackFeatureUsage } = useTutorialTrigger();

    const handleCloseDateRange = useCallback((e) => {
        e.preventDefault();
        dispatch(setIsComparisonRangeSet(false));
        handleClose();
        dispatch(setRemoveCompareFilter(false));
        e.stopPropagation();
    }, [dispatch, handleClose]);

    const handleNotesClick = useCallback(() => {
        trackFeatureUsage(TUTORIAL_FEATURES.NOTES_FEATURE).catch((error) => {
            console.error('Failed to track notes feature usage:', error);
        });

        triggerTutorial(TUTORIAL_FEATURES.NOTES_FEATURE).catch((error) => {
            console.error('Failed to trigger notes feature tutorial:', error);
        });

        if (handleOnclickOpenNotes) {
            handleOnclickOpenNotes();
        }
    }, [handleOnclickOpenNotes, trackFeatureUsage, triggerTutorial]);

    const handleTrendAnalysisClick = useCallback(() => {
        trackFeatureUsage(TUTORIAL_FEATURES.SPOTTING_TRENDS).catch((error) => {
            console.error('Failed to track spotting trends usage:', error);
        });

        triggerTutorial(TUTORIAL_FEATURES.SPOTTING_TRENDS).catch((error) => {
            console.error('Failed to trigger spotting trends tutorial:', error);
        });

        if (handleOnclickCompare) {
            handleOnclickCompare();
        }
    }, [handleOnclickCompare, trackFeatureUsage, triggerTutorial]);

    return (
        <MenuList>
            {page !== PAGE_TYPE.OVERALL && (
                <MenuSwitchItem
                    icon={<NotificationsActiveOutlinedIcon />}
                    label="Manage Alerts"
                    onClick={handleOnclickOpenAlertReport}
                    handleClose={handleClose}
                    handleSaveShortcut={handleSaveShortcut}
                    checked={selectedSortCut.isAlertsReport}
                    name="isAlertsReport"
                    isMobile={isMobile}
                />
            )}

            <MenuSwitchItem
                icon={
                    projectionDays !== null ? (
                        <TooltipBadge
                            content="Remove Projection Filter"
                            onClick={(e) => {
                                handleClose();
                                e.stopPropagation();
                                dispatch(setProjectionDays(null));
                            }}
                        >
                            <IconButton
                                sx={{
                                    width: 30,
                                    height: 30,
                                    ml: "-5px",
                                    backgroundColor: '#4879f5',
                                    borderRadius: "4px",
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                    },
                                    color: '#fff',
                                }}
                            >
                                <PickProjectionDaysSVG />
                            </IconButton>
                        </TooltipBadge>
                    ) : (
                        <PickProjectionDaysSVG />
                    )
                }
                label="Time Frame Converter"
                onClick={() => {
                    trackFeatureUsage(TUTORIAL_FEATURES.TIME_FRAME_CONVERTER).catch((error) => {
                        console.error('Failed to track time frame converter usage:', error);
                    });

                    triggerTutorial(TUTORIAL_FEATURES.TIME_FRAME_CONVERTER).catch((error) => {
                        console.error('Failed to trigger time frame converter tutorial:', error);
                    });

                    if (typeof handleOnclickTimeFrameConvert === 'function') {
                        handleOnclickTimeFrameConvert();
                    }
                }}
                handleClose={handleClose}
                handleSaveShortcut={handleSaveShortcut}
                checked={selectedSortCut.isPickProjectionDays}
                name="isPickProjectionDays"
                isMobile={isMobile}
            />
            <MenuSwitchItem
                icon={
                    rangesSet ? (
                        <TooltipBadge
                            content="Remove Compare Filter"
                            onClick={handleCloseDateRange}
                        >
                            <IconButton
                                sx={{
                                    width: 30,
                                    height: 30,
                                    ml: "-5px",
                                    backgroundColor: '#4879f5',
                                    borderRadius: "4px",
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                    },
                                    color: '#fff',
                                }}
                            >
                                <CompareSideIcon width={18} height={18} />
                            </IconButton>
                        </TooltipBadge>
                    ) : (
                        <CompareSideIcon />
                    )
                }
                label="Trend Analysis"
                onClick={handleTrendAnalysisClick}
                handleClose={handleClose}
                handleSaveShortcut={handleSaveShortcut}
                checked={selectedSortCut.isCompare}
                name="isCompare"
                isMobile={isMobile}
            />
            <MenuSwitchItem
                icon={<AutoModeIcon />}
                label="Automated Reports"
                onClick={handleOnclickOpenAutoReport}
                handleClose={handleClose}
                handleSaveShortcut={handleSaveShortcut}
                checked={selectedSortCut.isAutoReport}
                name="isAutoReport"
                isMobile={isMobile}
            />
            <MenuSwitchItem
                icon={<NotesIcon />}
                label="Notes"
                onClick={handleNotesClick}
                handleClose={handleClose}
                handleSaveShortcut={handleSaveShortcut}
                checked={selectedSortCut.isNotes}
                name="isNotes"
                isMobile={isMobile}
            />
            {isPageConverterPage(page, pageConverterTransferType) && typeof onPageConverter === "function" && (
                <MenuSwitchItem
                    icon={<SwapHorizIcon fontSize="small" />}
                    label="Convert to Admissions Page"
                    onClick={onPageConverter}
                    handleClose={handleClose}
                    handleSaveShortcut={handleSaveShortcut}
                    checked={!!selectedSortCut.isPageConverter}
                    name="isPageConverter"
                    isMobile={isMobile}
                    disabled={pageConverterDisabled}
                />
            )}
        </MenuList>
    );
}
